/**
 * Mar & Alma motion primitives.
 *
 * Four custom elements, each self-contained so the theme's importmap does not
 * need to know about them. Every one degrades to a static, fully readable
 * layout when the visitor prefers reduced motion, and every one drives layout
 * from real scroll position so `position: sticky` keeps working.
 */

const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The pinned scene and the sideways lookbook each trade a tall scroll track for
 * a single screen of content. On a phone that track runs to several times the
 * viewport height, and once the last pane has been reached the rest of it
 * scrolls past as empty screen. Below this width they lay out as ordinary
 * stacked sections instead. The number matches the breakpoint in the
 * stylesheets, so the CSS and the JS always agree on which mode is running.
 */
const NARROW = '(max-width: 820px)';
const staticMode = () => reduced() || window.matchMedia(NARROW).matches;

/** Clamp a number into a range. */
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/**
 * Some themes take the scroll away from the window. Horizon did: above 990px it
 * gave `html` and `body` `overflow: hidden` and handed scrolling to
 * `.page-wrapper`, and because scroll events do not bubble, a listener on
 * `window` never heard it and every scroll-driven section froze on its first
 * frame. Prestige does not, so this returns null and the window path is used.
 * Keeping it costs nothing and means the module survives the next theme.
 *
 * Returns whichever element is really scrolling, or null when it is the page.
 */
function scroller() {
  const wrapper = document.querySelector('.page-wrapper');
  if (!wrapper) return null;
  const style = getComputedStyle(wrapper);
  const scrolls = style.overflowY === 'auto' || style.overflowY === 'scroll';
  return scrolls && wrapper.scrollHeight > wrapper.clientHeight ? wrapper : null;
}

/**
 * Binds to the document in the capture phase, which sees scroll on any
 * descendant, and to the real scroller directly as a belt-and-braces second
 * path. Handlers here are all frame-throttled, so a double delivery costs
 * nothing.
 */
function onScrollAnywhere(handler) {
  document.addEventListener('scroll', handler, { passive: true, capture: true });
  scroller()?.addEventListener('scroll', handler, { passive: true });
  window.addEventListener('resize', handler, { passive: true });
}

function offScrollAnywhere(handler) {
  document.removeEventListener('scroll', handler, { capture: true });
  scroller()?.removeEventListener('scroll', handler);
  window.removeEventListener('resize', handler);
}

/** The height a pinned stage actually occupies, measured from the real scroller. */
function stageHeight() {
  return scroller()?.clientHeight || window.innerHeight;
}

/**
 * Progress of an element through the viewport, 0 before it starts and 1 once
 * its scrollable height is spent.
 */
function pinProgress(el) {
  const rect = el.getBoundingClientRect();
  const distance = rect.height - stageHeight();
  if (distance <= 0) return 0;
  return clamp(-rect.top / distance, 0, 1);
}

/* ── reveals ─────────────────────────────────────────────────────────── */

/**
 * Blocks ship hidden (`.ma-reveal` is `opacity: 0`, `.ma-mask > span` is pushed
 * out of its own overflow) and are brought in when they reach the viewport.
 *
 * This is deliberately one observer for the whole document rather than one per
 * <ma-reveal>. A merchant drops blocks into whichever section they like, and
 * most sections hold `{% content_for 'blocks' %}` directly, so scoping the
 * observer to an ancestor element left every one of those sections rendering
 * as an empty band of ground colour.
 */
const REVEAL_SELECTOR = '.ma-reveal, .ma-mask';

let revealObserver = null;

function revealObserverInstance() {
  if (revealObserver) return revealObserver;
  revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );
  return revealObserver;
}

/** Starts watching every hidden element inside `root`, and `root` itself. */
function watchReveals(root) {
  if (!root || root.nodeType !== 1) return;

  const targets = [...root.querySelectorAll(REVEAL_SELECTOR)];
  if (root.matches?.(REVEAL_SELECTOR)) targets.unshift(root);
  if (!targets.length) return;

  if (reduced() || !('IntersectionObserver' in window)) {
    targets.forEach((t) => t.classList.add('is-in'));
    return;
  }

  const observer = revealObserverInstance();
  targets.forEach((t) => {
    if (!t.classList.contains('is-in')) observer.observe(t);
  });
}

/**
 * Kept so a section can still mark its own subtree, and so the element in the
 * markup means something. The document-wide sweep below covers it either way.
 */
class MaReveal extends HTMLElement {
  connectedCallback() {
    watchReveals(this);
  }
}

/** Sweeps the page now, and again for anything Shopify renders in later. */
function startReveals() {
  document.documentElement.classList.add('ma-motion-ready');
  watchReveals(document.body);

  new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => watchReveals(node));
    });
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.body) {
  startReveals();
} else {
  document.addEventListener('DOMContentLoaded', startReveals, { once: true });
}

/* ── pinned scene ────────────────────────────────────────────────────── */

/**
 * A tall element whose inner stage sticks while panes are swapped by scroll
 * position. Under reduced motion the panes stack and the height collapses,
 * so the same content reads as an ordinary list of sections.
 */
class MaPin extends HTMLElement {
  connectedCallback() {
    this.panes = [...this.querySelectorAll('[data-ma-pane]')];
    this.bars = [...this.querySelectorAll('[data-ma-pane-bar]')];
    if (this.panes.length < 2) return;

    this.onScroll = this.onScroll.bind(this);
    this.applyMode = this.applyMode.bind(this);
    this.frame = null;
    this.isStatic = null;
    this.mq = window.matchMedia(NARROW);
    this.mq.addEventListener('change', this.applyMode);
    this.applyMode();
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this.applyMode);
    offScrollAnywhere(this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /** Stacked and fully readable, or pinned and scroll-driven. */
  applyMode() {
    const stat = staticMode();
    if (stat === this.isStatic) return;
    this.isStatic = stat;

    if (stat) {
      offScrollAnywhere(this.onScroll);
      this.setAttribute('data-ma-static', '');
      this.panes.forEach((p) => {
        p.classList.add('is-on');
        p.setAttribute('aria-hidden', 'false');
      });
      return;
    }

    this.removeAttribute('data-ma-static');
    onScrollAnywhere(this.onScroll);
    this.update();
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const progress = pinProgress(this);
    const index = clamp(Math.floor(progress * this.panes.length), 0, this.panes.length - 1);

    this.panes.forEach((pane, i) => {
      pane.classList.toggle('is-on', i === index);
      pane.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    });

    this.bars.forEach((bar, i) => {
      const local = clamp(progress * this.panes.length - i, 0, 1);
      bar.style.setProperty('--ma-bar', local);
    });
  }
}

/* ── horizontal lookbook ─────────────────────────────────────────────── */

/**
 * Turns vertical scroll into horizontal travel across a track. Falls back to a
 * normal horizontally scrollable rail under reduced motion, which keeps the
 * content reachable by keyboard and touch.
 */
class MaLookbook extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ma-track]');
    if (!this.track) return;

    this.onScroll = this.onScroll.bind(this);
    this.applyMode = this.applyMode.bind(this);
    this.frame = null;
    this.isStatic = null;
    this.mq = window.matchMedia(NARROW);
    this.mq.addEventListener('change', this.applyMode);
    this.applyMode();
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this.applyMode);
    offScrollAnywhere(this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /** A swipeable strip on a phone, a scroll-driven track on a desktop. */
  applyMode() {
    const stat = staticMode();
    if (stat === this.isStatic) return;
    this.isStatic = stat;

    if (stat) {
      offScrollAnywhere(this.onScroll);
      this.setAttribute('data-ma-static', '');
      this.track.style.transform = '';
      return;
    }

    this.removeAttribute('data-ma-static');
    onScrollAnywhere(this.onScroll);
    this.update();
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const travel = this.track.scrollWidth - this.clientWidth;
    if (travel <= 0) {
      this.track.style.transform = '';
      return;
    }
    this.track.style.transform = `translate3d(${-pinProgress(this) * travel}px, 0, 0)`;
  }
}

/* ── clip-path wipe ──────────────────────────────────────────────────── */

/** Reveals one layer over another as the section crosses the viewport. */
class MaWipe extends HTMLElement {
  connectedCallback() {
    this.layer = this.querySelector('[data-ma-wipe-layer]');
    if (!this.layer) return;

    if (reduced()) {
      this.setAttribute('data-ma-static', '');
      return;
    }

    this.onScroll = this.onScroll.bind(this);
    this.frame = null;
    onScrollAnywhere(this.onScroll);
    this.update();
  }

  disconnectedCallback() {
    offScrollAnywhere(this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    const rect = this.getBoundingClientRect();
    const view = stageHeight();
    const span = view + rect.height;
    const travelled = clamp((view - rect.top) / span, 0, 1);
    const eased = clamp((travelled - 0.15) / 0.6, 0, 1);
    this.layer.style.setProperty('--ma-wipe', `${(1 - eased) * 100}%`);
  }
}

/* ── counting numbers ────────────────────────────────────────────────── */

/** Counts a number up once, when it first comes into view. */
class MaCount extends HTMLElement {
  connectedCallback() {
    const target = Number(this.dataset.maTo || this.textContent.replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(target) || target === 0) return;

    const suffix = this.dataset.maSuffix || '';
    const render = (value) => {
      this.textContent = Math.round(value).toLocaleString() + suffix;
    };

    if (reduced() || !('IntersectionObserver' in window)) {
      render(target);
      return;
    }

    render(0);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        const started = performance.now();
        const duration = 1200;
        const step = (now) => {
          const t = clamp((now - started) / duration, 0, 1);
          render(target * (1 - Math.pow(1 - t, 3)));
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      },
      { threshold: 0.4 }
    );
    observer.observe(this);
  }
}

/**
 * Register each element on its own. If one ever throws while upgrading, the
 * others still come up rather than the whole page losing its motion.
 */
for (const [name, ctor] of [
  ['ma-reveal', MaReveal],
  ['ma-pin', MaPin],
  ['ma-lookbook', MaLookbook],
  ['ma-wipe', MaWipe],
  ['ma-count', MaCount],
]) {
  if (customElements.get(name)) continue;
  try {
    customElements.define(name, ctor);
  } catch (error) {
    console.error(`[Mar & Alma] could not define <${name}>`, error);
  }
}
